import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import StaffCapabilityDashboardPage from '../../pages/dashboard/StaffCapabilityDashboardPage';
import SuperadminDashboardPage from '../../pages/superadmin/SuperadminDashboardPage';
import SuperadminPlatformEntryPage from '../../pages/superadmin/SuperadminPlatformEntryPage';
import LoginPage from '../../pages/login/LoginPage';
import ConfirmEmailPage from '../../pages/auth/ConfirmEmailPage';
import NavStubPage from '../../pages/nav/NavStubPage';
import BootstrapErrorPage from '../../pages/system/BootstrapErrorPage';
import ForbiddenPage from '../../pages/system/ForbiddenPage';
import ModuleUnavailablePage from '../../pages/system/ModuleUnavailablePage';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTE_PATHS } from './route-paths';
import { StaffRootRedirect } from './StaffRootRedirect';
import FitnessLegacyRedirectPage from '../../pages/fitness/FitnessLegacyRedirectPage';

const OrganizationPage = lazy(() => import('../../pages/organization/OrganizationPage'));
const CrmSettingsPage = lazy(() => import('../../pages/settings/CrmSettingsPage'));
const OrganizationBranchesPage = lazy(() => import('../../pages/organization/OrganizationBranchesPage'));
const OrganizationServicesPage = lazy(() => import('../../pages/organization/OrganizationServicesPage'));
const SubscriptionPage = lazy(() => import('../../pages/subscription/SubscriptionPage'));
const OperationalPaymentsPage = lazy(() => import('../../pages/payments/OperationalPaymentsPage'));
const OrganizationsPage = lazy(() => import('../../pages/superadmin/OrganizationsPage'));
const TenantRequestsPage = lazy(() => import('../../pages/superadmin/TenantRequestsPage'));
const TenantRequestDetailsPage = lazy(() => import('../../pages/superadmin/TenantRequestDetailsPage'));
const OrganizationDetailsPage = lazy(() => import('../../pages/superadmin/OrganizationDetailsPage'));
const SuperadminPlansPage = lazy(() => import('../../pages/superadmin/SuperadminPlansPage'));
const PlatformUsersPage = lazy(() => import('../../pages/superadmin/PlatformUsersPage'));
const PlatformRolesPage = lazy(() => import('../../pages/superadmin/PlatformRolesPage'));
const SuperadminClientsCatalogPage = lazy(() => import('../../pages/superadmin/SuperadminClientsCatalogPage'));
const SuperadminUsersHub = lazy(() => import('../../pages/superadmin/SuperadminUsersHub'));
const SuperadminUsersHubIndexRedirect = lazy(() => import('../../pages/superadmin/SuperadminUsersHubIndexRedirect'));
const AuditLogPage = lazy(() => import('../../pages/journal/AuditLogPage'));
const TenantUsersHub = lazy(() => import('../../pages/admin/users/TenantUsersHub'));
const TenantUsersHubIndexRedirect = lazy(() => import('../../pages/admin/users/TenantUsersHubIndexRedirect'));
const StaffDirectoryPage = lazy(() => import('../../pages/admin/staff/StaffDirectoryPage'));
const OrganizationClientsPage = lazy(() => import('../../pages/clients/OrganizationClientsPage'));
const OrgRolesPage = lazy(() => import('../../pages/admin/rbac/OrgRolesPage'));
const ControlsPage = import.meta.env.DEV
  ? lazy(() => import('../../pages/dev/ControlsPage'))
  : null;
const badgeContrastFixtureEnabled =
  import.meta.env.DEV || import.meta.env.VITE_E2E_FIXTURES === '1';
const BadgeContrastFixturePage = badgeContrastFixtureEnabled
  ? lazy(() => import('../../pages/dev/BadgeContrastFixturePage'))
  : null;
const ServiceTypesPage = lazy(() => import('../../pages/service-catalog/ServiceTypesPage'));
const ServiceCatalogListPage = lazy(() => import('../../pages/service-catalog/ServiceCatalogListPage'));
const ServiceSpacesPage = lazy(() => import('../../pages/service-catalog/ServiceSpacesPage'));
const EquipmentPage = lazy(() => import('../../pages/equipment/EquipmentPage'));
const SubscriptionsPage = lazy(() => import('../../pages/subscriptions/SubscriptionsPage'));
const StaffDocsPage = lazy(() => import('../../pages/docs/StaffDocsPage'));
const StaffProfilePage = lazy(() => import('../../pages/profile/StaffProfilePage'));
const StaffContactPage = lazy(() => import('../../pages/contact/StaffContactPage'));
const ClientPortalPage = lazy(() => import('../../pages/feedback/ClientPortalPage'));
const GlobalWidgetThemePage = lazy(() => import('../../pages/global-widget/GlobalWidgetThemePage'));
const StaffApplicationPage = lazy(() => import('../../pages/application/StaffApplicationPage'));
const StaffTrainersPage = lazy(() => import('../../pages/trainers/StaffTrainersPage'));
const StaffPendingAuthPage = lazy(() => import('../../pages/pending-auth/StaffPendingAuthPage'));
const StaffIncomingRequestsPage = lazy(() => import('../../pages/incoming-requests/StaffIncomingRequestsPage'));
const StaffAutomationSourcesPage = lazy(() => import('../../pages/automation-sources/StaffAutomationSourcesPage'));
const StaffAutomationIncomingPage = lazy(() => import('../../pages/automation-incoming/StaffAutomationIncomingPage'));
const StaffPaymentGatewaysPage = lazy(() => import('../../pages/payment-gateways/StaffPaymentGatewaysPage'));
const StaffAuditPaymentsPage = lazy(() => import('../../pages/audit-payments/StaffAuditPaymentsPage'));
const StaffAdminTokensPage = lazy(() => import('../../pages/admin-tokens/StaffAdminTokensPage'));
const YcSyncSummaryPage = lazy(() => import('../../pages/yclients-sync/YcSyncSummaryPage'));
const YcSyncSettingsPage = lazy(() => import('../../pages/yclients-sync/YcSyncSettingsPage'));

const YcSyncLogPage = lazy(() => import('../../pages/yclients-sync/YcSyncLogPage'));
const YcSyncConflictsPage = lazy(() => import('../../pages/yclients-sync/YcSyncConflictsPage'));
const YcUnmappedItemsPage = lazy(() => import('../../pages/yclients-sync/YcUnmappedItemsPage'));
const YcDiscoverBranchesPage = lazy(() => import('../../pages/yclients-sync/YcDiscoverBranchesPage'));
const ScheduleTrainingsPage = lazy(() => import('../../pages/schedule/ScheduleTrainingsPage'));
const ScheduleWorkhoursPage = lazy(() => import('../../pages/schedule/ScheduleWorkhoursPage'));
const BookingsPage = lazy(() => import('../../pages/bookings/BookingsPage'));
const ClientsBookingsPage = lazy(() => import('../../pages/bookings/ClientsBookingsPage'));
const StaffTariffsPage = lazy(() => import('../../pages/tariffs/StaffTariffsPage'));
const PublicPricingPage = lazy(() => import('../../pages/tariffs/PublicPricingPage'));
const PublicSupportPage = lazy(() => import('../../pages/support/PublicSupportPage'));
const PublicLegalDocPage = lazy(() => import('../../pages/legal/PublicLegalDocPage'));
const BecomeTenantPage = lazy(() => import('../../pages/tenant-request/BecomeTenantPage'));
const TenantRequestPortalPage = lazy(() => import('../../pages/tenant-request/TenantRequestPortalPage'));
const StaffWidgetEmbedLabPage = lazy(() => import('../../pages/widget-embed-lab/StaffWidgetEmbedLabPage'));
const StaffSalesFunnelPage = lazy(() => import('../../pages/sales-funnel/StaffSalesFunnelPage'));
const StaffSalesFunnelSettingsPage = lazy(() => import('../../pages/sales-funnel/StaffSalesFunnelSettingsPage'));
const BillingHistoryPage = lazy(() => import('../../pages/billing/BillingHistoryPage'));
const QrCodesPage = lazy(() => import('../../pages/qrcode-logs/QrCodesPage'));
const QrCodeSettingsPage = lazy(() => import('../../pages/qrcode-settings/QrCodeSettingsPage'));
const StaffTasksPage = lazy(() => import('../../pages/tasks/StaffTasksPage'));
const AssistantPage = lazy(() => import('../../pages/assistant/AssistantPage'));
const SuperadminAssistantPage = lazy(() => import('../../pages/superadmin/SuperadminAssistantPage'));
const SuperadminQRCodeConfigPage = lazy(() => import('../../pages/superadmin/SuperadminQRCodeConfigPage'));
const SuperadminPaymentsPage = lazy(() => import('../../pages/superadmin/SuperadminPaymentsPage'));

export function AppRoutes() {
  return (
    <Routes>
      <Route path={ROUTE_PATHS.login} element={<LoginPage />} />
      <Route path={ROUTE_PATHS.confirmEmail} element={<ConfirmEmailPage />} />
      <Route path={ROUTE_PATHS.pricing} element={<PublicPricingPage />} />
      <Route path={ROUTE_PATHS.support} element={<PublicSupportPage />} />
      <Route path={ROUTE_PATHS.publicOffer} element={<PublicLegalDocPage docKind="offer" />} />
      <Route path={ROUTE_PATHS.tos} element={<PublicLegalDocPage docKind="tos" />} />
      <Route path={ROUTE_PATHS.gdpr} element={<PublicLegalDocPage docKind="privacy" />} />
      <Route path="/offer" element={<Navigate to={ROUTE_PATHS.publicOffer} replace />} />
      <Route path="/terms" element={<Navigate to={ROUTE_PATHS.tos} replace />} />
      <Route path="/privacy" element={<Navigate to={ROUTE_PATHS.gdpr} replace />} />
      {badgeContrastFixtureEnabled && BadgeContrastFixturePage ? (
        <Route
          path={ROUTE_PATHS.badgeContrastFixture}
          element={
            <Suspense fallback={null}>
              <BadgeContrastFixturePage />
            </Suspense>
          }
        />
      ) : null}
      <Route path={ROUTE_PATHS.becomeTenant} element={<BecomeTenantPage />} />
      <Route path={ROUTE_PATHS.tenantRequestPortal} element={<TenantRequestPortalPage />} />
      <Route path={ROUTE_PATHS.forbidden} element={<ForbiddenPage />} />
      <Route path={ROUTE_PATHS.moduleUnavailable} element={<ModuleUnavailablePage />} />
      <Route path={ROUTE_PATHS.bootstrapError} element={<BootstrapErrorPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AdminLayout />}>
          <Route path={ROUTE_PATHS.root} element={<StaffRootRedirect />} />
          <Route path={ROUTE_PATHS.superadminDashboard} element={<SuperadminDashboardPage />} />
          <Route path={ROUTE_PATHS.tenantDashboard} element={<StaffCapabilityDashboardPage />} />
          <Route path={ROUTE_PATHS.fitnessDashboard} element={<Navigate to={ROUTE_PATHS.staffDashboard} replace />} />
          <Route path={ROUTE_PATHS.fitness} element={<FitnessLegacyRedirectPage />} />
          <Route path={ROUTE_PATHS.staffDashboard} element={<StaffCapabilityDashboardPage />} />
          <Route path={ROUTE_PATHS.superadmin} element={<SuperadminPlatformEntryPage />} />
          <Route path={ROUTE_PATHS.superadminOrganizationDetailPattern} element={<OrganizationDetailsPage />} />
          <Route path={ROUTE_PATHS.superadminOrganizations} element={<OrganizationsPage />} />
          <Route path={ROUTE_PATHS.superadminTenantRequests} element={<TenantRequestsPage />} />
          <Route path={ROUTE_PATHS.superadminTenantRequestsDetailPattern} element={<TenantRequestDetailsPage />} />
          <Route path={ROUTE_PATHS.superadminPlans} element={<SuperadminPlansPage />} />
          <Route path={ROUTE_PATHS.superadminPayments} element={<SuperadminPaymentsPage />} />
          <Route path={ROUTE_PATHS.superadminAssistant} element={<SuperadminAssistantPage />} />
          <Route path={ROUTE_PATHS.superadminUsers} element={<SuperadminUsersHub />}>
            <Route index element={<SuperadminUsersHubIndexRedirect />} />
            <Route path="platform" element={<PlatformUsersPage />} />
            <Route path="clients" element={<SuperadminClientsCatalogPage />} />
            <Route path="roles" element={<PlatformRolesPage />} />
          </Route>
          <Route
            path={ROUTE_PATHS.superadminPlatformUsers}
            element={<Navigate to={ROUTE_PATHS.superadminUsersPlatform} replace />}
          />
          <Route
            path={ROUTE_PATHS.superadminClientsCatalog}
            element={<Navigate to={ROUTE_PATHS.superadminUsersClients} replace />}
          />
          <Route path={ROUTE_PATHS.auditLog} element={<AuditLogPage />} />
          <Route path={ROUTE_PATHS.staffDirectory} element={<TenantUsersHub />}>
            <Route index element={<TenantUsersHubIndexRedirect />} />
            <Route path="staff" element={<StaffDirectoryPage />} />
            <Route path="clients" element={<OrganizationClientsPage />} />
            <Route path="trainers" element={<StaffTrainersPage />} />
            <Route path="access-rbac" element={<OrgRolesPage />} />
          </Route>
          <Route path="/all-users" element={<Navigate to={ROUTE_PATHS.tenantUsersStaffTab} replace />} />
          <Route path={ROUTE_PATHS.clients} element={<Navigate to={ROUTE_PATHS.tenantUsersClientsTab} replace />} />
          <Route path={ROUTE_PATHS.accessRbac} element={<Navigate to={ROUTE_PATHS.tenantUsersAccessRbacTab} replace />} />
          <Route path={ROUTE_PATHS.organization} element={<OrganizationPage />} />
          <Route path={ROUTE_PATHS.organizationProfileEdit} element={<OrganizationPage />} />
          <Route
            path={ROUTE_PATHS.organizationSettings}
            element={<Navigate to={ROUTE_PATHS.organizationProfileEdit} replace />}
          />
          <Route path={ROUTE_PATHS.crmSettings} element={<CrmSettingsPage />} />
          <Route path={ROUTE_PATHS.organizationBranches} element={<OrganizationBranchesPage />} />
          <Route path={ROUTE_PATHS.organizationServices} element={<OrganizationServicesPage />} />
          <Route path={ROUTE_PATHS.servicesTypes} element={<ServiceTypesPage />} />
          <Route path={ROUTE_PATHS.servicesList} element={<ServiceCatalogListPage />} />
          <Route path={ROUTE_PATHS.servicesSpaces} element={<ServiceSpacesPage />} />
          <Route path={ROUTE_PATHS.equipment} element={<EquipmentPage />} />
          <Route path={ROUTE_PATHS.subscriptions} element={<SubscriptionsPage />} />
          <Route path={ROUTE_PATHS.schedule} element={<ScheduleTrainingsPage />} />
          <Route path={ROUTE_PATHS.scheduleTrainings} element={<Navigate to={ROUTE_PATHS.schedule} replace />} />
          <Route path={ROUTE_PATHS.scheduleWorkhours} element={<ScheduleWorkhoursPage />} />
          <Route
            path={`${ROUTE_PATHS.scheduleWorkhours}/weekly`}
            element={<Navigate to={ROUTE_PATHS.scheduleWorkhours} replace />}
          />
          <Route path={ROUTE_PATHS.subscription} element={<SubscriptionPage />} />
          <Route path={ROUTE_PATHS.payments} element={<OperationalPaymentsPage />} />
          <Route path={ROUTE_PATHS.bookings} element={<BookingsPage />} />
          <Route path={ROUTE_PATHS.clientsBookings} element={<ClientsBookingsPage />} />
          <Route path={ROUTE_PATHS.docs} element={<StaffDocsPage />} />
          <Route path={ROUTE_PATHS.me} element={<StaffProfilePage />} />
          <Route path={ROUTE_PATHS.contact} element={<StaffContactPage />} />
          <Route path={ROUTE_PATHS.feedback} element={<ClientPortalPage />} />
          <Route path={ROUTE_PATHS.globalWidgetTheme} element={<GlobalWidgetThemePage />} />
          <Route path={ROUTE_PATHS.application} element={<StaffApplicationPage />} />
          <Route path={ROUTE_PATHS.trainers} element={<Navigate to={ROUTE_PATHS.tenantUsersTrainersTab} replace />} />
          <Route path={ROUTE_PATHS.trainersLegacy} element={<Navigate to={ROUTE_PATHS.tenantUsersTrainersTab} replace />} />
          <Route path={ROUTE_PATHS.pendingAuth} element={<StaffPendingAuthPage />} />
          <Route path={ROUTE_PATHS.incomingRequests} element={<StaffIncomingRequestsPage />} />
          <Route path={ROUTE_PATHS.automationSources} element={<StaffAutomationSourcesPage />} />
          <Route path={ROUTE_PATHS.automationIncoming} element={<StaffAutomationIncomingPage />} />
          <Route path={ROUTE_PATHS.paymentGateways} element={<StaffPaymentGatewaysPage />} />
          <Route path={ROUTE_PATHS.auditPayments} element={<StaffAuditPaymentsPage />} />
          <Route path={ROUTE_PATHS.adminTokens} element={<StaffAdminTokensPage />} />
          <Route path={ROUTE_PATHS.ycSyncSummary} element={<YcSyncSummaryPage />} />
          <Route path={ROUTE_PATHS.ycSyncSettings} element={<YcSyncSettingsPage />} />
          <Route path={ROUTE_PATHS.ycSyncLog} element={<YcSyncLogPage />} />
          <Route path={ROUTE_PATHS.ycSyncConflicts} element={<YcSyncConflictsPage />} />
          <Route path={ROUTE_PATHS.ycUnmapped} element={<YcUnmappedItemsPage />} />
          <Route path={ROUTE_PATHS.ycDiscoverBranches} element={<YcDiscoverBranchesPage />} />
          {ControlsPage && (
            <Route path={ROUTE_PATHS.controls} element={<ControlsPage />} />
          )}
          <Route path={ROUTE_PATHS.tariffs} element={<StaffTariffsPage />} />
          <Route path={ROUTE_PATHS.billing} element={<BillingHistoryPage />} />
          <Route path={ROUTE_PATHS.widgetEmbedLab} element={<StaffWidgetEmbedLabPage />} />
          <Route path={ROUTE_PATHS.salesFunnel} element={<StaffSalesFunnelPage />} />
          <Route path={ROUTE_PATHS.salesFunnelSettings} element={<StaffSalesFunnelSettingsPage />} />
          <Route path={ROUTE_PATHS.qrcode} element={<QrCodesPage />} />
          <Route path={ROUTE_PATHS.qrcodeSettings} element={<QrCodeSettingsPage />} />
          <Route path={ROUTE_PATHS.tasks} element={<StaffTasksPage />} />
          <Route path={ROUTE_PATHS.assistant} element={<AssistantPage />} />
          <Route path={ROUTE_PATHS.superadminQRCodeConfig} element={<SuperadminQRCodeConfigPage />} />
          <Route path="*" element={<NavStubPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
